/**
 * Fuel card transaction upload (Control Centre Fuel).
 * Same eflex ingest path as Data Feed; upload history persists across refreshes.
 */

import { useEffect, useRef, useState } from 'react';
import { CheckCircle, Loader2, Upload, X } from 'lucide-react';
import {
  FUEL_TRANSACTION_ACCEPT,
  detectFuelCsvPeriod,
  fileToCsvTextForFuel,
  formatEflexUploadMessage,
  isFuelTransactionFile,
  uploadEflexFuelRows,
  uploadEflexFuelTransactions,
  type EflexSkippedRow,
} from '../../utils/fuelTransactionUpload';
import {
  applyAcceptedSkipsToHistoryRow,
  loadFuelUploadHistory,
  saveFuelUploadHistory,
  type FuelUploadHistoryRow,
  type FuelUploadHistoryStatus,
} from '../../utils/fuelTransactionUploadHistory';
import { Button } from '../ui/button';

export type { FuelUploadHistoryRow, FuelUploadHistoryStatus };

/** Header (~2rem) + 4 body rows with View button (~6.25rem each). */
const UPLOAD_HISTORY_VIEWPORT_CLASS = 'max-h-upload-history-4';

const MOCK_HISTORY: FuelUploadHistoryRow[] = [
  {
    id: 'mock-1',
    status: 'parsed',
    filename: 'fuelcard_week36.csv',
    periodLabel: '01–07 Sep',
    rows: 412,
    skippedCount: 0,
  },
  {
    id: 'mock-2',
    status: 'warn',
    filename: 'fuelcard_week35.csv',
    periodLabel: '25–31 Aug',
    rows: 398,
    detail: '3 rows skipped',
    skippedCount: 3,
    skippedRows: [
      {
        row: 12,
        reason: 'empty_registration',
        reason_label: 'No vehicle registration',
        type: 'Drawing',
        product: 'Keyfuels Diesel',
        date: '28/08/2026',
        unit_price: 145.2,
        total_price_gbp: 48.1,
        quantity: 33.1,
      },
      {
        row: 18,
        reason: 'adblue',
        reason_label: 'AdBlue (not diesel fill)',
        type: 'Drawing',
        product: 'Keyfuels Adblue Can',
        registration: 'AB12CDE',
        date: '29/08/2026',
        unit_price: 80,
        total_price_gbp: 12.5,
        quantity: 15.6,
        candidate: {
          transactionId: 'mock-adblue-18',
          vehicleRegistration: 'AB12CDE',
          transactionDateTime: '29/08/2026 10:00:00',
          quantity: 15.6,
          unitPrice: 80,
          totalPriceGbp: 12.5,
          companyProductName: 'Keyfuels Adblue Can',
          transType: 'Drawing',
          authority: 'declared',
          transportMode: 'road',
          cardNumber: '****0476',
        },
      },
      {
        row: 41,
        reason: 'not_drawing',
        reason_label: 'Not a Drawing (e.g. fee, sundry, toll)',
        type: 'Sale Sundry',
        product: 'Card Charge',
        date: '30/08/2026',
        unit_price: 750,
        total_price_gbp: 7.5,
        quantity: 1,
      },
    ],
  },
  {
    id: 'mock-3',
    status: 'failed',
    filename: 'week34_export.xlsx',
    periodLabel: '—',
    rows: null,
    detail: "Column headers didn't match template",
  },
];

function formatPeriodLabel(start: string, end: string): string {
  const fmt = (ymd: string) => {
    const d = new Date(`${ymd}T00:00:00.000Z`);
    if (Number.isNaN(d.getTime())) return ymd;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  };
  if (start === end) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatUnitPrice(n?: number): string {
  return n == null || !Number.isFinite(n) ? '—' : n.toFixed(1);
}

function formatTotalPrice(n?: number): string {
  return n == null || !Number.isFinite(n) ? '—' : `£${n.toFixed(2)}`;
}

function statusBadge(status: FuelUploadHistoryStatus): { label: string; className: string } {
  if (status === 'parsed') {
    return { label: 'Parsed', className: 'bg-success/15 text-success' };
  }
  if (status === 'warn') {
    return { label: 'Warning', className: 'bg-warning/15 text-warning' };
  }
  return { label: 'Failed', className: 'bg-destructive/15 text-destructive' };
}

interface FuelTransactionsUploadPanelProps {
  useMockData?: boolean;
  onUploaded?: () => void;
}

export default function FuelTransactionsUploadPanel({
  useMockData = false,
  onUploaded,
}: FuelTransactionsUploadPanelProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [stagedPeriod, setStagedPeriod] = useState<{ start: string; end: string } | null>(null);
  const [history, setHistory] = useState<FuelUploadHistoryRow[]>(() => {
    if (useMockData) return MOCK_HISTORY;
    return loadFuelUploadHistory().history;
  });
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [skipModalRow, setSkipModalRow] = useState<FuelUploadHistoryRow | null>(null);
  const [acceptedSkipRows, setAcceptedSkipRows] = useState<Set<number>>(() => new Set());
  const [skipSaving, setSkipSaving] = useState(false);
  const [skipSaveError, setSkipSaveError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stageGenRef = useRef(0);
  const acceptGenRef = useRef(0);
  const persistReadyRef = useRef(false);

  useEffect(() => {
    if (useMockData) return;
    if (!persistReadyRef.current) {
      persistReadyRef.current = true;
      return;
    }
    saveFuelUploadHistory({ history });
  }, [history, useMockData]);

  const openSkipModal = (row: FuelUploadHistoryRow) => {
    acceptGenRef.current += 1;
    setSkipModalRow(row);
    setAcceptedSkipRows(new Set());
    setSkipSaveError(null);
  };

  const closeSkipModal = () => {
    acceptGenRef.current += 1;
    setSkipModalRow(null);
    setAcceptedSkipRows(new Set());
    setSkipSaveError(null);
    setSkipSaving(false);
  };

  const toggleAcceptSkip = (spreadsheetRow: number, enabled: boolean) => {
    if (!enabled) return;
    setAcceptedSkipRows((prev) => {
      const next = new Set(prev);
      if (next.has(spreadsheetRow)) next.delete(spreadsheetRow);
      else next.add(spreadsheetRow);
      return next;
    });
  };

  const acceptableSkipRowNums = (skipModalRow?.skippedRows ?? [])
    .filter((s) => Boolean(s.candidate))
    .map((s) => s.row);
  const allAcceptableSelected =
    acceptableSkipRowNums.length > 0
    && acceptableSkipRowNums.every((row) => acceptedSkipRows.has(row));

  const toggleAcceptAllSkips = () => {
    if (acceptableSkipRowNums.length === 0) return;
    setAcceptedSkipRows((prev) => {
      const allSelected = acceptableSkipRowNums.every((row) => prev.has(row));
      if (allSelected) return new Set();
      return new Set(acceptableSkipRowNums);
    });
  };

  const handleSaveAcceptedSkips = async () => {
    const modalSnapshot = skipModalRow;
    if (!modalSnapshot?.skippedRows) return;
    const selected = modalSnapshot.skippedRows.filter(
      (s) => acceptedSkipRows.has(s.row) && s.candidate,
    );
    if (selected.length === 0) {
      setSkipSaveError('Select at least one Accept checkbox for a row that can be queued.');
      return;
    }

    const gen = ++acceptGenRef.current;
    setSkipSaving(true);
    setSkipSaveError(null);
    try {
      if (!useMockData) {
        const { response, json } = await uploadEflexFuelRows(
          selected.map((s) => s.candidate!),
        );
        if (!response.ok) {
          throw new Error(json.error || json.message || `HTTP ${response.status}`);
        }
      }

      // Modal closed / switched while request was in flight — do not mutate history.
      if (gen !== acceptGenRef.current) return;

      const acceptedNums = selected.map((s) => s.row);
      const updated = applyAcceptedSkipsToHistoryRow(modalSnapshot, acceptedNums);
      setHistory((prev) => prev.map((h) => (h.id === updated.id ? updated : h)));
      setSuccessMessage(
        `Queued ${selected.length} accepted skipped row(s) from “${modalSnapshot.filename}”.`,
      );
      onUploaded?.();
      if (updated.skippedRows && updated.skippedRows.length > 0) {
        setSkipModalRow(updated);
        setAcceptedSkipRows(new Set());
      } else {
        closeSkipModal();
      }
    } catch (err) {
      if (gen !== acceptGenRef.current) return;
      setSkipSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      if (gen === acceptGenRef.current) {
        setSkipSaving(false);
      }
    }
  };
  const stageFile = async (file: File) => {
    const gen = ++stageGenRef.current;
    setError(null);
    setSuccessMessage(null);
    if (!isFuelTransactionFile(file)) {
      setSelectedFile(null);
      setStagedPeriod(null);
      setError('Fuel transactions expect .csv, .xlsx, or .xls.');
      return;
    }
    setSelectedFile(file);
    try {
      const csv = await fileToCsvTextForFuel(file);
      if (gen !== stageGenRef.current) return;
      setStagedPeriod(detectFuelCsvPeriod(csv));
    } catch {
      if (gen !== stageGenRef.current) return;
      setStagedPeriod(null);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setStagedPeriod(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const pushHistory = (row: FuelUploadHistoryRow) => {
    setHistory((prev) =>
      [{ ...row, uploadedAt: row.uploadedAt ?? new Date().toISOString() }, ...prev].slice(0, 40),
    );
  };

  const handleUpload = async () => {
    setError(null);
    setSuccessMessage(null);
    if (!selectedFile) {
      setError('Select a fuel card export first.');
      return;
    }

    if (useMockData) {
      const period = stagedPeriod ?? { start: '2026-09-01', end: '2026-09-07' };
      pushHistory({
        id: `demo-${Date.now()}`,
        status: 'parsed',
        filename: selectedFile.name,
        periodLabel: formatPeriodLabel(period.start, period.end),
        rows: 120,
      });
      setSuccessMessage(`Demo mode — recorded “${selectedFile.name}” locally (no ingest).`);
      clearFile();
      return;
    }

    setIsUploading(true);
    try {
      const { response, json, csv } = await uploadEflexFuelTransactions(selectedFile);
      const period = detectFuelCsvPeriod(csv) ?? stagedPeriod;

      if (!response.ok) {
        pushHistory({
          id: `fail-${Date.now()}`,
          status: 'failed',
          filename: selectedFile.name,
          periodLabel: period ? formatPeriodLabel(period.start, period.end) : '—',
          rows: null,
          detail: json.error || json.message || `HTTP ${response.status}`,
          file: selectedFile,
          skippedRows: json.skipped_rows,
          skippedRowsTruncated: json.skipped_rows_truncated,
          skippedCount: typeof json.skipped === 'number' ? json.skipped : undefined,
        });
        throw new Error(
          json.error || json.message || `Upload failed with HTTP ${response.status}`,
        );
      }

      const formatted = formatEflexUploadMessage(selectedFile.name, json);
      const skipped = typeof json.skipped === 'number' ? json.skipped : 0;
      const status: FuelUploadHistoryStatus =
        json.status === 'empty' || formatted.kind === 'empty'
          ? 'failed'
          : skipped > 0
            ? 'warn'
            : 'parsed';

      const historyId = json.job_id || `ok-${Date.now()}`;
      pushHistory({
        id: historyId,
        status,
        filename: selectedFile.name,
        periodLabel: period ? formatPeriodLabel(period.start, period.end) : '—',
        rows: typeof json.items_queued === 'number' ? json.items_queued : null,
        detail:
          status === 'warn'
            ? `${skipped} rows skipped`
            : status === 'failed'
              ? 'No diesel Drawing rows with registration'
              : undefined,
        skippedRows: json.skipped_rows,
        skippedRowsTruncated: json.skipped_rows_truncated,
        skippedCount: skipped,
      });
      clearFile();
      if (status === 'failed') {
        setError(formatted.text);
      } else {
        setSuccessMessage(formatted.text);
        onUploaded?.();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const timedOut = /timed out|TimeoutError|aborted/i.test(msg);
      setError(
        timedOut
          ? 'Upload timed out while ZeeCore parsed or queued the file. Try again, or check that ZeeCore is running.'
          : msg,
      );
    } finally {
      setIsUploading(false);
    }
  };

  const retryRow = (row: FuelUploadHistoryRow) => {
    if (row.file) void stageFile(row.file).then(() => undefined);
    else setError('Original file is no longer available — drop the export again.');
  };

  return (
    <div className="bg-card/50 rounded-lg border border-border p-5 flex flex-col min-h-0 h-full">
      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
        <Upload className="w-4 h-4 text-chart-2" />
        Fuel transactions
      </h4>
      <p className="text-xs text-muted-foreground mt-1 mb-3">Fuel card exports (Keyfuels / eflex).</p>

      <div
        role="button"
        tabIndex={isUploading ? -1 : 0}
        className={`group relative cursor-pointer rounded-2xl border-2 border-dashed p-5 text-center transition-all duration-250 ease-out ${
          isUploading ? 'pointer-events-none opacity-60' : ''
        } ${
          isDragging || selectedFile
            ? 'border-solid border-chart-2 bg-background'
            : 'border-border bg-background hover:border-chart-2'
        }`}
        onClick={() => {
          if (!isUploading) fileInputRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!isUploading) fileInputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (file) void stageFile(file);
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={FUEL_TRANSACTION_ACCEPT}
          className="hidden"
          disabled={isUploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void stageFile(file);
          }}
        />
        <div
          className={`mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full border-2 transition-all duration-250 ${
            isDragging || selectedFile
              ? 'border-chart-2 bg-background'
              : 'border-border bg-background group-hover:border-chart-2'
          }`}
        >
          <Upload className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-base font-bold text-white mb-1">Drop CSV/XLSX export</p>
        <p className="text-sm text-muted-foreground">
          Drag and drop, or click to browse — period is auto-detected from transaction dates
        </p>

        {selectedFile && (
          <div
            className="mt-4 mx-auto max-w-sm flex items-center gap-2 rounded-lg border border-chart-2/40 bg-background/80 px-3 py-2 text-left"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white truncate">{selectedFile.name}</p>
              <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024).toFixed(1)} KB</p>
            </div>
            <Button
              variant="ghost"
              size="iconSm"
              type="button"
              disabled={isUploading}
              onClick={(e) => {
                e.stopPropagation();
                clearFile();
              }}
              className="rounded text-muted-foreground hover:text-foreground"
              aria-label="Remove file"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      <div className="mt-3 flex justify-end">
        <Button
          variant="default"
          size="sm"
          type="button"
          disabled={isUploading || !selectedFile}
          onClick={() => void handleUpload()}
          className="bg-chart-2 hover:bg-chart-2 font-semibold"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Uploading…
            </>
          ) : (
            'Upload'
          )}
        </Button>
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-destructive/40 bg-destructive/40 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mt-3 rounded-lg border border-success/40 bg-success/30 px-3 py-2 text-xs text-success flex items-start gap-2">
          <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="whitespace-pre-line">{successMessage}</span>
        </div>
      )}

      <div className="mt-4">
        <h5 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Upload history</h5>
        <div className={`overflow-x-auto overflow-y-auto rounded-md border border-border/60 ${UPLOAD_HISTORY_VIEWPORT_CLASS}`}>
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-background z-raised">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border h-8">
                <th className="py-0 pr-2 pl-2 font-semibold">Status</th>
                <th className="py-0 pr-2 font-semibold">File</th>
                <th className="py-0 pr-2 font-semibold">Period</th>
                <th className="py-0 pr-2 font-semibold text-right">Rows</th>
                <th className="py-0 pr-2 font-semibold text-right">Rows skipped</th>
                <th className="py-0 pr-2 font-semibold text-right" />
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-4 text-muted-foreground text-center">
                    No uploads yet
                  </td>
                </tr>
              ) : (
                history.map((row) => {
                  const badge = statusBadge(row.status);
                  const hasSkipDetails = Boolean(row.skippedRows && row.skippedRows.length > 0);
                  const skippedDisplay =
                    typeof row.skippedCount === 'number'
                      ? row.skippedCount
                      : hasSkipDetails
                        ? row.skippedRows!.length
                        : null;
                  const skippedPositive = typeof skippedDisplay === 'number' && skippedDisplay > 0;
                  return (
                    <tr key={row.id} className="border-b border-border/80 align-top">
                      <td className="py-2 pr-2 pl-2">
                        <span className={`inline-flex text-xs font-bold px-2 py-0.5 rounded ${badge.className}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-2 pr-2">
                        <div className="font-medium text-white">{row.filename}</div>
                        {row.detail && !hasSkipDetails && (
                          <div className="text-xs text-muted-foreground mt-0.5">{row.detail}</div>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-ring/90 font-semibold whitespace-nowrap">
                        {row.periodLabel}
                      </td>
                      <td className="py-2 pr-2 text-right tabular-nums text-foreground">
                        {row.rows == null ? '—' : row.rows}
                      </td>
                      <td className="py-2 pr-2 text-right">
                        <div
                          className={`tabular-nums ${
                            skippedPositive ? 'text-warning font-semibold' : 'text-foreground'
                          }`}
                        >
                          {skippedDisplay == null ? '—' : skippedDisplay}
                        </div>
                        {hasSkipDetails && (
                          <Button
                            variant="warning"
                            size="sm"
                            type="button"
                            onClick={() => openSkipModal(row)}
                            className="mt-1 rounded border border-warning/50 bg-warning/10 text-warning hover:bg-warning/20 hover:border-warning/70 font-semibold"
                          >
                            View skipped rows
                          </Button>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-right">
                        {row.status === 'failed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            onClick={() => retryRow(row)}
                            className="rounded px-2 py-1 font-semibold"
                          >
                            Retry
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {skipModalRow?.skippedRows && skipModalRow.skippedRows.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6"
          onClick={closeSkipModal}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="skipped-rows-title"
            className="bg-popover rounded-lg border border-border shadow-2xl w-upload-modal h-upload-modal overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
              <div className="min-w-0">
                <h3 id="skipped-rows-title" className="text-xl font-bold text-white">
                  Skipped spreadsheet rows
                </h3>
                <p className="text-sm text-muted-foreground mt-0.5 truncate" title={skipModalRow.filename}>
                  {skipModalRow.filename}
                  {skipModalRow.skippedRowsTruncated
                    ? ` · showing first ${skipModalRow.skippedRows.length} of ${skipModalRow.skippedCount ?? skipModalRow.skippedRows.length}`
                    : ` · ${skipModalRow.skippedCount ?? skipModalRow.skippedRows.length} skipped`}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Accept is only for AdBlue / non-diesel product rows that still have a registration.
                  Fees, tolls, and empty registration cannot be queued.
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={closeSkipModal}
                className="rounded-lg shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </Button>
            </div>
            <div className="flex-1 overflow-auto px-5 py-4">
              <div className="rounded border border-border/80 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background z-raised">
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                      <th className="py-2.5 px-3 font-semibold">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-border bg-card text-chart-2 focus:ring-chart-2/40 disabled:opacity-40"
                            checked={allAcceptableSelected}
                            disabled={acceptableSkipRowNums.length === 0 || skipSaving}
                            title={
                              acceptableSkipRowNums.length === 0
                                ? 'No AdBlue / non-diesel rows can be accepted'
                                : allAcceptableSelected
                                  ? 'Clear all'
                                  : 'Accept all eligible AdBlue / non-diesel rows'
                            }
                            onChange={toggleAcceptAllSkips}
                            aria-label="Accept all eligible skipped rows"
                          />
                          <span>Accept</span>
                        </label>
                      </th>
                      <th className="py-2.5 px-3 font-semibold">Row</th>
                      <th className="py-2.5 px-3 font-semibold">Why</th>
                      <th className="py-2.5 px-3 font-semibold">Type</th>
                      <th className="py-2.5 px-3 font-semibold">Product</th>
                      <th className="py-2.5 px-3 font-semibold">Reg</th>
                      <th className="py-2.5 px-3 font-semibold">Date</th>
                      <th className="py-2.5 px-3 font-semibold text-right">Unit price</th>
                      <th className="py-2.5 px-3 font-semibold text-right">Total price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skipModalRow.skippedRows.map((s: EflexSkippedRow) => {
                      const canAccept = Boolean(s.candidate);
                      const checked = acceptedSkipRows.has(s.row);
                      return (
                        <tr key={`${skipModalRow.id}-${s.row}-${s.reason}`} className="border-b border-border">
                          <td className="py-2.5 px-3">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-border bg-card text-chart-2 focus:ring-chart-2/40 disabled:opacity-40"
                              checked={checked}
                              disabled={!canAccept || skipSaving}
                              title={
                                canAccept
                                  ? 'Queue this AdBlue / non-diesel row as a declared fuel transaction'
                                  : 'Only AdBlue / non-diesel rows with registration can be accepted'
                              }
                              onChange={() => toggleAcceptSkip(s.row, canAccept)}
                              aria-label={`Accept spreadsheet row ${s.row}`}
                            />
                          </td>
                          <td className="py-2.5 px-3 tabular-nums text-foreground">{s.row}</td>
                          <td className="py-2.5 px-3 text-warning/90">{s.reason_label}</td>
                          <td className="py-2.5 px-3 text-muted-foreground">{s.type || '—'}</td>
                          <td className="py-2.5 px-3 text-muted-foreground max-w-48 truncate" title={s.product}>
                            {s.product || '—'}
                          </td>
                          <td className="py-2.5 px-3 text-foreground">{s.registration || '—'}</td>
                          <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">{s.date || '—'}</td>
                          <td className="py-2.5 px-3 text-right tabular-nums text-foreground">
                            {formatUnitPrice(s.unit_price)}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums text-foreground">
                            {formatTotalPrice(s.total_price_gbp)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-border bg-background/40">
              <div className="min-w-0">
                {skipSaveError ? (
                  <p className="text-xs text-destructive">{skipSaveError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {acceptedSkipRows.size > 0
                      ? `${acceptedSkipRows.size} row(s) selected`
                      : 'No rows selected'}
                  </p>
                )}
              </div>
              <Button
                variant="default"
                size="sm"
                type="button"
                disabled={skipSaving || acceptedSkipRows.size === 0}
                onClick={() => void handleSaveAcceptedSkips()}
                className="bg-chart-2 hover:bg-chart-2 font-semibold"
              >
                {skipSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  'Save & queue accepted'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
